import { useEffect, useState } from "react";
import { useAdmin } from "../admin";
import { englishTitle, nextStep, percent, reasons } from "../lib/dashboard";
import type { Dashboard as Data } from "../lib/dashboard";
import { roleName, skillName } from "../lib/scenario";
import { errorText, supabase } from "../supabase";

// Screen 12: the operations head compares sites and spots weak areas at a glance.

const dateText = (iso: string) =>
  new Date(iso + "T00:00:00").toLocaleDateString("en-IN", { day: "numeric", month: "short" });

export default function Dashboard() {
  const { sites } = useAdmin();
  const [site, setSite] = useState("");
  const [period, setPeriod] = useState<"week" | "month">("week");
  const [data, setData] = useState<Data | null>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let live = true;
    setError("");
    supabase.rpc("admin_dashboard", { p_site: site || null, p_period: period }).then(({ data, error }) => {
      if (!live) return;
      if (error) setError(errorText(error));
      else setData(data as Data);
    });
    return () => {
      live = false;
    };
  }, [site, period]);

  return (
    <>
      <div className="page-head">
        <h1>Practice dashboard</h1>
        {data?.sample && <span className="chip sample">Sample data</span>}
        <select aria-label="Site" value={site} onChange={(e) => setSite(e.target.value)} style={{ width: "auto" }}>
          <option value="">All sites</option>
          {sites.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </select>
        <div className="segmented" role="group" aria-label="Period">
          <button className={period === "week" ? "on" : ""} onClick={() => setPeriod("week")}>
            This week
          </button>
          <button className={period === "month" ? "on" : ""} onClick={() => setPeriod("month")}>
            This month
          </button>
        </div>
      </div>

      {error && <div className="notice error">{error}</div>}
      {!data && !error && <div className="empty">Loading…</div>}
      {data && <Body data={data} />}

      <p className="footer-note">Scores guide coaching. They are never used for discipline or pay.</p>
    </>
  );
}

function Body({ data }: { data: Data }) {
  const since = data.period === "week" ? `Week from ${dateText(data.from)}` : `Weeks from ${dateText(data.from)}`;
  if (data.people.total === 0) {
    return (
      <div className="card empty">
        No one is on the roster here yet. Import the roster on the People page, and numbers will show once people
        start practising.
      </div>
    );
  }
  if (data.practices.assigned === 0) {
    return (
      <div className="card empty">
        No practice has been given out yet for this period. Approved scenarios are given out every night, for this
        week and next.
      </div>
    );
  }
  const best = data.bestFirst;
  return (
    <div className="stack" style={{ gap: 20 }}>
      <div className="grid-3">
        <div className="card dark">
          <p className="muted small">Practice done · {since}</p>
          <p className="big-number">{percent(data.practices.done, data.practices.assigned)}%</p>
          <p>
            {data.practices.done} of {data.practices.assigned} practices · {data.people.done} of {data.people.total}{" "}
            people finished
          </p>
        </div>
        <div className="card">
          <p className="muted small">Best answer at the first choice</p>
          {best ? (
            <>
              <p className="big-number">{percent(best.best, best.tries)}%</p>
              <p className="muted">
                {best.best} of {best.tries} first choices, by {best.people} people
              </p>
            </>
          ) : (
            <>
              <p className="big-number muted">–</p>
              <p className="muted">Shown once at least three people have practised.</p>
            </>
          )}
        </div>
        <div className="card">
          <p className="muted small">May need a coaching conversation</p>
          <p className="big-number">{data.coaching.length}</p>
          <p className="muted">{data.coaching.length === 1 ? "person" : "people"}, listed below</p>
        </div>
      </div>

      <div className="grid-2">
        <div className="card">
          <h2>Completion by site</h2>
          {data.sites.map((s) => {
            const pct = percent(s.practices.done, s.practices.assigned);
            return (
              <div className="bar-row" key={s.siteId}>
                <span>{s.name}</span>
                <div className="bar" role="img" aria-label={`${pct}% done`}>
                  <div style={{ width: `${pct}%` }} />
                </div>
                <span className="small muted">
                  {s.practices.assigned ? `${pct}% · ${s.people.done}/${s.people.total} people` : "Nothing given yet"}
                </span>
              </div>
            );
          })}
        </div>
        <div className="card">
          <h2>Hardest scenarios</h2>
          {data.hardest.length === 0 ? (
            <p className="muted">Not enough practice yet. A scenario shows here once three or more people have tried it.</p>
          ) : (
            <div className="stack">
              {data.hardest.map((h) => (
                <div key={h.scenarioId}>
                  <p>
                    <strong>{englishTitle(h.title, h.scenarioId)}</strong>
                  </p>
                  <p className="small muted">
                    {h.missedFirst} of {h.people} people missed the best answer at first
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="card table-card">
        <div style={{ padding: "12px 12px 4px" }}>
          <h2>Staff who may need a coaching conversation</h2>
          <p className="muted small">
            From last week's practice and the first choices of the last four weeks. Listed by site and name, not
            ranked.
          </p>
        </div>
        {data.coaching.length === 0 ? (
          <p className="empty">No one right now.</p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Staff</th>
                <th>Site</th>
                <th>Done</th>
                <th>Weakest area</th>
                <th>Suggested next step</th>
              </tr>
            </thead>
            <tbody>
              {data.coaching.map((p) => (
                <tr key={p.staffId}>
                  <td>
                    <strong>{p.name}</strong>
                    <div className="small muted">
                      {roleName(p.role)} · {p.phone}
                    </div>
                  </td>
                  <td>{p.site}</td>
                  <td>
                    {p.done} of {p.assigned}
                  </td>
                  <td>{p.weakSkill ? skillName(p.weakSkill) : <span className="muted">–</span>}</td>
                  <td>
                    {nextStep(p)}
                    <div className="small muted">{reasons(p).join(" · ")}</div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
